import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RosterPage() {
  const [wrestlers, setWrestlers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/wrestlers`)
      .then(r => setWrestlers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div data-testid="roster-page" className="aged-paper">
      {/* Segment header */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="segment-label">Talent</div>
            <div className="flex-1 h-[1px] bg-foreground/15" />
          </div>
          <h1 data-testid="roster-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-4">
            The Roster
          </h1>
          <div className="h-[3px] w-16 gold-accent" />
        </div>
      </section>

      <div className="vintage-divider" />

      {wrestlers.length === 0 ? (
        /* Coming soon — only shows when roster is empty */
        <section className="py-24 sm:py-36 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-block bg-[#0D0D0D] px-8 py-5 mb-8">
              <p className="font-heading text-xl sm:text-2xl text-[#B09030] uppercase tracking-wider">
                Talent Announcements
              </p>
            </div>
            <p data-testid="roster-coming-soon" className="font-heading text-2xl sm:text-3xl uppercase leading-snug mb-8">
              The territory is assembling<br/>its finest competitors.
            </p>
            <div className="h-[3px] w-16 gold-accent mx-auto mb-8" />
            <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
              Check back soon.
            </p>
          </div>
        </section>
      ) : (
        /* Wrestler roster grid */
        <section className="py-16 sm:py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {wrestlers.map(w => (
                <div key={w.id} data-testid={`wrestler-card-${w.id}`} className="group">
                  {/* Photo */}
                  <div className="bg-[#0D0D0D] aspect-[3/4] overflow-hidden relative">
                    {w.image_url ? (
                      <img
                        src={w.image_url}
                        alt={w.name}
                        className="w-full h-full object-cover vintage-img group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <p className="font-heading text-5xl text-[#B09030]/20 uppercase">{w.name.charAt(0)}</p>
                      </div>
                    )}
                    {/* Champion badge */}
                    {w.is_champion && (
                      <div className="absolute top-3 left-3 gold-accent px-3 py-1">
                        <p className="font-heading text-[10px] text-[#1A1510] uppercase tracking-wider">
                          {w.championship_title || "Champion"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Info bar */}
                  <div className="bg-[#0D0D0D] p-4 border-t-[3px] border-[#B09030]">
                    <h3 className="font-heading text-lg text-[#E8DFC8] uppercase leading-tight">
                      {w.name}
                    </h3>
                    {w.nickname && (
                      <p className="font-heading text-sm text-[#B09030] uppercase mt-1">
                        "{w.nickname}"
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2 font-body text-[10px] text-[#E8DFC8]/40 uppercase tracking-wider">
                      {w.hometown && <span>{w.hometown}</span>}
                      {w.weight_class && (
                        <>
                          <span className="text-[#E8DFC8]/15">|</span>
                          <span>{w.weight_class}</span>
                        </>
                      )}
                      {(w.wins > 0 || w.losses > 0) && (
                        <>
                          <span className="text-[#E8DFC8]/15">|</span>
                          <span>{w.wins}W-{w.losses}L</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Bio */}
                  {w.bio && (
                    <div className="bg-muted p-4">
                      <p className="font-body text-sm text-foreground/70 leading-relaxed">
                        {w.bio}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
